import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { fetchSipCredentials } from '../api';
import { setAgentAvailable, setAgentBusy, setAgentOffline } from '../lib/agentPresence';
import type { CallQueueItem } from '../types';

type VobizConnectionStatus = 'offline' | 'connecting' | 'registered' | 'calling' | 'in_call' | 'error';

interface SoftphoneProps {
  agentId: string;
  presenceEnabled?: boolean;
  activeQueueCall?: CallQueueItem | null;
  activeCall?: CallQueueItem | null;
  onCallEnded?: () => void;
}

interface StoredCredentials {
  username: string;
  password: string;
}

interface VobizClient {
  on: (event: string, handler: (...args: any[]) => void) => void;
  login: (username: string, password: string) => void;
  logout: () => void;
  answer: () => void;
  reject: () => void;
  hangup: () => void;
  mute: () => void;
  unmute: () => void;
  remoteView?: HTMLAudioElement | HTMLVideoElement;
  getPeerConnection?: () => { pc?: RTCPeerConnection };
}

interface VobizInstance {
  client: VobizClient;
}

export interface BridgeData {
  room_name?: string | null;
  token?: string | null;
  ws_url?: string | null;
  call_uuid?: string | null;
  sip_bridge_ready?: boolean | null;
}

export interface SoftphoneHandle {
  answerAssignedCall: (callUuid: string, bridgeData?: BridgeData | null) => Promise<void>;
  stopBridge: () => void;
}

interface ProviderIncomingCallInfo {
  rawCall: any | null;
  callerName: string | null;
  callId: string | null;
  callUuid: string | null;
}

function isProviderCallObject(candidate: unknown): candidate is Record<string, any> {
  if (!candidate || typeof candidate !== 'object') return false;
  const call = candidate as Record<string, any>;
  return (
    typeof call.answer === 'function' &&
    typeof call.reject === 'function' &&
    typeof call.hangup === 'function'
  );
}

function extractProviderCall(args: any[]): Record<string, any> | null {
  const candidates = args.filter((arg) => arg && typeof arg === 'object') as Record<string, any>[];

  const richCall = [...candidates].reverse().find((candidate) =>
    isProviderCallObject(candidate) &&
    (typeof candidate.getPeerConnection === 'function' || 'remoteView' in candidate || 'options' in candidate)
  );
  if (richCall) return richCall;

  const anyCall = [...candidates].reverse().find((candidate) => isProviderCallObject(candidate));
  if (anyCall) return anyCall;

  return null;
}

declare global {
  interface Window {
    Vobiz?: new (options: Record<string, unknown>) => VobizInstance;
  }
}

const SDK_SRC = 'https://cdn.jsdelivr.net/npm/vobiz-webrtc-sdk';

const getStatusLabel = (status: VobizConnectionStatus) => {
  switch (status) {
    case 'connecting':
      return 'Connecting';
    case 'registered':
      return 'Ready';
    case 'calling':
      return 'Calling';
    case 'in_call':
      return 'In call';
    case 'error':
      return 'Error';
    case 'offline':
    default:
      return 'Offline';
  }
};

const getStatusClass = (status: VobizConnectionStatus) => {
  switch (status) {
    case 'registered':
      return 'bg-emerald-500';
    case 'calling':
      return 'bg-amber-500 animate-pulse';
    case 'in_call':
      return 'bg-blue-500';
    case 'error':
      return 'bg-red-500';
    case 'connecting':
      return 'bg-yellow-500 animate-pulse';
    case 'offline':
    default:
      return 'bg-yellow-500';
  }
};

const loadVobizSdk = () => {
  if (window.Vobiz) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>(`script[src="${SDK_SRC}"]`);
  if (existing) {
    return new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Vobiz WebRTC SDK')), { once: true });
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Vobiz WebRTC SDK'));
    document.head.appendChild(script);
  });
};

const requestMicrophoneAccess = async () => {
  if (!navigator.mediaDevices?.getUserMedia) return;
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach((track) => track.stop());
};

export const Softphone = forwardRef<SoftphoneHandle, SoftphoneProps>(
  ({ agentId, presenceEnabled = false, activeQueueCall = null, activeCall = null, onCallEnded }, ref) => {
  const [status, setStatus] = useState<VobizConnectionStatus>('offline');
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [speakerBoost, setSpeakerBoost] = useState(2);
  const [error, setError] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [needsPermissions, setNeedsPermissions] = useState(false);
  const vobizRef = useRef<VobizInstance | null>(null);
  const livekitRoomRef = useRef<Room | null>(null);
  const livekitAudioElementsRef = useRef<HTMLAudioElement[]>([]);
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const autoConnectAttemptedRef = useRef(false);
  const providerIncomingCallRef = useRef<ProviderIncomingCallInfo | null>(null);
  const activeQueueCallRef = useRef<CallQueueItem | null>(activeQueueCall);

  useEffect(() => {
    activeQueueCallRef.current = activeQueueCall;
  }, [activeQueueCall]);

  const markAvailable = () => {
    if (!presenceEnabled) return;
    setAgentAvailable({ email: agentId }).catch(() => undefined);
  };

  const markBusy = (callUuid: string | null = null) => {
    if (!presenceEnabled) return;
    setAgentBusy({ email: agentId }, callUuid).catch(() => undefined);
  };

  const markOffline = () => {
    if (!presenceEnabled) return;
    setAgentOffline({ email: agentId }).catch(() => undefined);
  };

  const requestPermissions = async () => {
    // Mark that we've prompted the user
    localStorage.setItem('softphone_permissions_prompted', 'true');
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (err) {
        console.warn('[NOTIF_PERMISSION] request failed:', err);
      }
    }
    
    // Request microphone permission
    try {
      await requestMicrophoneAccess();
    } catch (error) {
      console.warn('[MIC_PERMISSION] request failed:', error);
      setError('Microphone permission is required to answer browser calls.');
      return;
    }
    
    setNeedsPermissions(false);
    connect();
  };

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    if (status === 'in_call') {
      timer = setInterval(() => setCallDuration((prev) => prev + 1), 1000);
    } else {
      setCallDuration(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [status]);

  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = speakerBoost;
    }
  }, [speakerBoost]);

  const cleanupRemoteAudio = () => {
    sourceNodeRef.current?.disconnect();
    gainNodeRef.current?.disconnect();
    audioContextRef.current?.close().catch(() => undefined);
    sourceNodeRef.current = null;
    gainNodeRef.current = null;
    audioContextRef.current = null;
  };

  const cleanupLiveKitBridge = (disconnectRoom = true) => {
    livekitAudioElementsRef.current.forEach((element) => {
      try {
        element.pause();
      } catch {
        // ignore
      }
      element.srcObject = null;
      element.remove();
    });
    livekitAudioElementsRef.current = [];

    if (disconnectRoom && livekitRoomRef.current) {
      const room = livekitRoomRef.current;
      livekitRoomRef.current = null;
      room.disconnect().catch(() => undefined);
    } else if (!disconnectRoom) {
      livekitRoomRef.current = null;
    }
  };

  const stopBridge = () => {
    cleanupRemoteAudio();
    cleanupLiveKitBridge();
  };

  const playRemoteStream = (stream: MediaStream) => {
    const audioElement = audioElementRef.current;
    if (!audioElement) return;

    cleanupRemoteAudio();

    audioElement.srcObject = stream;
    audioElement.volume = 1;

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      const sourceNode = audioContext.createMediaStreamSource(stream);
      const gainNode = audioContext.createGain();

      gainNode.gain.value = speakerBoost;
      sourceNode.connect(gainNode);
      gainNode.connect(audioContext.destination);

      audioContextRef.current = audioContext;
      sourceNodeRef.current = sourceNode;
      gainNodeRef.current = gainNode;
      audioElement.muted = true;
      return;
    } catch (err) {
      console.warn('Speaker boost unavailable, falling back to normal browser volume:', err);
      audioElement.muted = false;
      audioElement.play().catch(() => undefined);
    }
  };

  const attachRemoteAudio = () => {
    window.setTimeout(() => {
      const vobiz = vobizRef.current;
      if (!vobiz) return;

      const remoteView = vobiz.client.remoteView;
      const remoteStream = remoteView?.srcObject;
      if (remoteStream instanceof MediaStream) {
        playRemoteStream(remoteStream);
        return;
      }

      try {
        const pc = vobiz.client.getPeerConnection?.().pc;
        const audioTrack = pc?.getReceivers().find((receiver) => receiver.track?.kind === 'audio')?.track;
        if (audioTrack) {
          playRemoteStream(new MediaStream([audioTrack]));
        }
      } catch {
        // The SDK exposes remote audio differently across builds.
      }
    }, 1200);
  };

  const connect = async () => {
    
    try {
      setStatus('connecting');
      setError(null);
      await loadVobizSdk();
      
      // Request notification permission alongside microphone permission
      if ('Notification' in window && Notification.permission === 'default') {
        try {
          await Notification.requestPermission();
        } catch (err) {
          console.warn('[NOTIF_PERMISSION] request failed on login:', err);
        }
      }
      
      try {
        await requestMicrophoneAccess();
      } catch (error) {
        console.warn('[MIC_PERMISSION] request failed on login:', error);
        setError('Microphone permission is required to answer browser calls.');
      }

      if (!window.Vobiz) {
        throw new Error('Vobiz WebRTC SDK is unavailable.');
      }

      const vobiz = new window.Vobiz({
        debug: 'ERROR',
        permOnClick: false,
        enableTracking: false,
        closeProtection: true,
        maxAverageBitrate: 48000,
      });

      vobiz.client.on('onWebrtcNotSupported', () => {
        console.error('WebRTC not supported');
        setStatus('error');
        setError('This browser does not support WebRTC calling.');
      });

      vobiz.client.on('onLogin', () => {
        setStatus('registered');
        setError(null);
        markAvailable();
      });

      vobiz.client.on('onLoginFailed', (reason?: string) => {
        console.error('=== SOFTPHONE LOGIN FAILED ===', reason);
        setStatus('error');
        setError(reason || 'Vobiz endpoint login failed.');
      });

      vobiz.client.on('onLogout', () => {
        cleanupRemoteAudio();
        cleanupLiveKitBridge();
        setStatus('offline');
        setIsMuted(false);
        markOffline();
        providerIncomingCallRef.current = null;
      });

      vobiz.client.on('onIncomingCall', (...incomingArgs: any[]) => {
        const callerName = typeof incomingArgs[0] === 'string' ? incomingArgs[0] : null;
        const rawCall = extractProviderCall(incomingArgs);
        const callId = rawCall ? String((rawCall as Record<string, any>).id ?? (rawCall as Record<string, any>).callId ?? '') || null : null;
        const callUuid = rawCall ? String((rawCall as Record<string, any>).callUuid ?? (rawCall as Record<string, any>).call_uuid ?? '') || null : null;
        providerIncomingCallRef.current = {
          rawCall,
          callerName,
          callId,
          callUuid,
        };
        const assignedQueueCall = activeQueueCallRef.current;
        if (!assignedQueueCall) {
        } else {
        }
      });

      vobiz.client.on('onIncomingCallCanceled', () => {
        setStatus('registered');
        providerIncomingCallRef.current = null;
        cleanupLiveKitBridge();
      });

      vobiz.client.on('onCallRemoteRinging', () => {
        setStatus('calling');
      });

      vobiz.client.on('onCallAnswered', () => {
        setStatus('in_call');
        markBusy(null);
        attachRemoteAudio();
        providerIncomingCallRef.current = null;
      });

      vobiz.client.on('onCallTerminated', () => {
        cleanupRemoteAudio();
        cleanupLiveKitBridge();
        setStatus('registered');
        setIsMuted(false);
        markAvailable();
        providerIncomingCallRef.current = null;
        onCallEnded?.();
      });

      vobiz.client.on('onCallFailed', (callInfo?: { reason?: string }) => {
        console.error('=== SOFTPHONE CALL FAILED ===', callInfo);
        cleanupRemoteAudio();
        cleanupLiveKitBridge();
        setStatus('registered');
        setIsMuted(false);
        markAvailable();
        providerIncomingCallRef.current = null;
        if (callInfo?.reason) setError(callInfo.reason);
        onCallEnded?.();
      });

      vobiz.client.on('onMediaPermission', (granted?: boolean) => {
        if (!granted) setError('Microphone permission is required to answer browser calls.');
      });

      vobizRef.current = vobiz;
      const creds = await fetchSipCredentials().catch(() => null);
      const username = creds?.username || '';
      const password = creds?.password || '';
      if (!username || !password) {
        setStatus('error');
        setError('SIP credentials not provisioned on server.');
        return;
      }
      vobiz.client.login(username, password);
    } catch (err) {
      console.error('=== SOFTPHONE CONNECT ERROR ===', err);
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to connect Vobiz WebRTC.');
    }
  };

  const disconnect = () => {
    vobizRef.current?.client.logout();
    vobizRef.current = null;
    cleanupRemoteAudio();
    cleanupLiveKitBridge();
    setStatus('offline');
    setIsMuted(false);
    providerIncomingCallRef.current = null;
    markOffline();
    onCallEnded?.();
  };

  const hangup = () => {
    vobizRef.current?.client.hangup();
    cleanupRemoteAudio();
    cleanupLiveKitBridge();
    setStatus('registered');
    setIsMuted(false);
    markAvailable();
    providerIncomingCallRef.current = null;
    onCallEnded?.();
  };

  const toggleMute = () => {
    if (!vobizRef.current) return;
    if (isMuted) {
      vobizRef.current.client.unmute();
      setIsMuted(false);
    } else {
      vobizRef.current.client.mute();
      setIsMuted(true);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const sanitizePhone = (phone: string) => phone.replace(/[^\d+]/g, '');
  const activeCallPhone = sanitizePhone(activeCall?.customerPhone || activeCall?.leadPhone || '');
  const activeCallPhoneLabel = activeCallPhone ? activeCallPhone : 'Unknown Number';
  const minimizedPhoneLabel = activeCallPhone
    ? activeCallPhone.slice(-4).padStart(activeCallPhone.length, '•')
    : 'Unknown';

  const copyPatientNumber = async () => {
    if (!activeCallPhone) return;
    try {
      await navigator.clipboard.writeText(activeCallPhone);
      setCopyFeedback('Copied');
      window.setTimeout(() => setCopyFeedback(null), 1200);
    } catch (error) {
      console.error('Failed to copy patient number:', error);
      setCopyFeedback('Copy failed');
      window.setTimeout(() => setCopyFeedback(null), 1200);
    }
  };

  useEffect(() => {
    setIsMinimized(false);
    setCopyFeedback(null);
  }, [activeCall?.id]);

  useEffect(() => {
    if (!presenceEnabled) {
      return;
    }

    if (autoConnectAttemptedRef.current || status !== 'offline') {
      return;
    }

    // Check if permissions are needed
    const checkPermissions = async () => {
      const needsNotif = 'Notification' in window && Notification.permission === 'default';
      
      // Check microphone permission using Permissions API if available
      let needsMic = true;
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          needsMic = result.state === 'prompt';
        } catch (e) {
        }
      }
      
      const alreadyPrompted = localStorage.getItem('softphone_permissions_prompted') === 'true';
      
      
      // Only show banner if permissions are needed and we haven't prompted before
      if ((needsMic || needsNotif) && !alreadyPrompted) {
        setNeedsPermissions(true);
        autoConnectAttemptedRef.current = true;
      } else {
        // Auto-connect if permissions are already granted
        autoConnectAttemptedRef.current = true;
        const timer = window.setTimeout(() => {
          connect();
        }, 800);
      }
    };
    
    checkPermissions();
  }, [presenceEnabled, status]);

  const attachLiveKitRemoteAudio = (room: Room) => {
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind !== Track.Kind.Audio) return;
      try {
        const element = track.attach() as HTMLAudioElement;
        element.autoplay = true;
        element.dataset.livekitAudio = 'true';
        element.style.display = 'none';
        document.body.appendChild(element);
        livekitAudioElementsRef.current.push(element);
        element.play().catch(() => undefined);
      } catch (error) {
        console.error('[BRIDGE] failed to attach LiveKit track', error);
      }
    });

    room.on(RoomEvent.Disconnected, () => {
      cleanupLiveKitBridge(false);
    });
  };

  const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const answerAssignedCall = async (callUuid: string, bridgeData?: BridgeData | null) => {
    stopBridge();

    const timeoutMs = 3000;
    const startedAt = Date.now();
    setStatus('connecting');

    while (Date.now() - startedAt < timeoutMs) {
      const providerCall = providerIncomingCallRef.current?.rawCall;
      const livekitReady = Boolean(bridgeData?.room_name && bridgeData?.token && bridgeData?.ws_url);

      if (livekitReady) {
        try {
          const room = new Room({});
          livekitRoomRef.current = room;
          attachLiveKitRemoteAudio(room);
          await room.connect(String(bridgeData?.ws_url), String(bridgeData?.token));
          await room.startAudio().catch(() => undefined);
          try {
            await room.localParticipant.setMicrophoneEnabled(true);
          } catch (error) {
            console.warn('[BRIDGE] microphone enable failed:', error);
          }
          setStatus('in_call');
          providerIncomingCallRef.current = null;
          return;
        } catch (error) {
          const reason = error instanceof Error ? error.message : String(error);
          console.error('[BRIDGE] failed:', reason);
          cleanupLiveKitBridge();
          throw error;
        }
      }

      if (providerCall && typeof providerCall.answer === 'function') {
        try {
          await Promise.resolve(providerCall.answer());
          setStatus('in_call');
          attachRemoteAudio();
          providerIncomingCallRef.current = null;
          return;
        } catch (error) {
          const reason = error instanceof Error ? error.message : String(error);
          console.error('[BRIDGE] failed:', reason);
          throw error;
        }
      }

      const vobizAnswer = vobizRef.current?.client?.answer;
      if (typeof vobizAnswer === 'function') {
        try {
          await Promise.resolve(vobizAnswer.call(vobizRef.current?.client));
          setStatus('in_call');
          attachRemoteAudio();
          providerIncomingCallRef.current = null;
          return;
        } catch (error) {
          const reason = error instanceof Error ? error.message : String(error);
          console.error('[BRIDGE] failed:', reason);
          throw error;
        }
      }

      await sleep(250);
    }

    const reason = 'No provider call object or LiveKit bridge data available';
    console.error('[BRIDGE] failed after timeout', reason);
    throw new Error(reason);
  };

  useImperativeHandle(ref, () => ({
    answerAssignedCall,
    stopBridge,
  }));

  return (
    <>
      {needsPermissions && (
        <div className="fixed top-4 left-4 right-4 z-50 mx-auto max-w-md rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-lg">
          <div className="mb-3 text-sm font-semibold text-blue-900">
            Enable Notifications
          </div>
          <div className="mb-3 text-xs text-blue-800">
            Please allow notification and microphone permissions to enable calling features.
          </div>
          <button
            type="button"
            onClick={requestPermissions}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Allow Permissions
          </button>
        </div>
      )}

      <div className="fixed top-4 right-4 z-40 flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-lg backdrop-blur">
        <span className={`h-2.5 w-2.5 rounded-full ${getStatusClass(status)}`} />
        <span>{getStatusLabel(status)}</span>
      </div>

      {status === 'in_call' && activeCall && (
        <div
          className={`fixed bottom-20 right-4 z-30 w-[calc(100vw-2rem)] max-w-sm overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl ${
            isMinimized ? 'p-0' : ''
          }`}
        >
          {isMinimized ? (
            <button
              type="button"
              onClick={() => setIsMinimized(false)}
              className="flex w-full items-center gap-3 bg-slate-950 px-4 py-3 text-left text-white"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-lg">📞</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold">Active Call</span>
                  <span className="font-mono text-sm text-slate-200">{formatTime(callDuration)}</span>
                </div>
                <div className="truncate text-xs text-slate-300">{minimizedPhoneLabel}</div>
              </div>
              <span className="rounded-full bg-white/10 px-2 py-1 text-xs font-semibold text-white">Open</span>
            </button>
          ) : (
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-r from-slate-950 to-slate-800 px-4 py-3 text-white">
                <div>
                  <div className="text-sm font-semibold">Active Call</div>
                  <div className="text-xs text-slate-300">Connected and live</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-slate-200">{formatTime(callDuration)}</span>
                  <button
                    type="button"
                    onClick={() => setIsMinimized(true)}
                    className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white hover:bg-white/20"
                    title="Minimize"
                  >
                    Minimize
                  </button>
                </div>
              </div>

              <div className="space-y-4 px-4 py-4">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Patient</div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="truncate text-xl font-bold text-slate-900">{activeCallPhoneLabel}</span>
                        <button
                          type="button"
                          onClick={copyPatientNumber}
                          disabled={!activeCallPhone}
                          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Copy
                        </button>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">{activeCall.leadName || 'Patient call in progress'}</div>
                    </div>
                    {copyFeedback && <div className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">{copyFeedback}</div>}
                  </div>
                </div>

                <div className="space-y-3 rounded-2xl bg-blue-50 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-900">Speaker Boost</span>
                    <span className="font-mono text-sm font-semibold text-blue-800">{speakerBoost.toFixed(1)}x</span>
                  </div>
                  <label className="block text-xs font-semibold text-blue-900">
                    <input
                      type="range"
                      min="1"
                      max="4"
                      step="0.25"
                      value={speakerBoost}
                      onChange={(event) => setSpeakerBoost(Number(event.target.value))}
                      className="mt-1 w-full accent-blue-600"
                    />
                  </label>
                </div>

                <audio ref={audioElementRef} autoPlay playsInline />

                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={toggleMute}
                    className="rounded-xl bg-slate-100 px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                  >
                    {isMuted ? 'Unmute' : 'Mute'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsMinimized(true)}
                    className="rounded-xl bg-slate-100 px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                  >
                    Minimize
                  </button>
                  <button
                    type="button"
                    onClick={hangup}
                    className="rounded-xl bg-red-600 px-3 py-3 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    Hang Up
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </>
  );
  },
);
